// assets/js/parking-options.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const isEditor = document.body.classList.contains("wp-admin");
if (!isEditor) gsap.registerPlugin(ScrollTrigger);

function toggleItem(item, open) {
  const btn = item.querySelector(".parking-options__trigger");
  const panel = item.querySelector(".parking-options__panel");
  if (!btn || !panel) return;

  item.classList.toggle("is-open", open);
  btn.setAttribute("aria-expanded", open ? "true" : "false");

  if (open) {
    panel.hidden = false;
    const h = panel.scrollHeight;

    gsap.fromTo(
      panel,
      { height: 0, opacity: 0 },
      {
        height: h,
        opacity: 1,
        duration: 0.35,
        ease: "power2.out",
        onComplete: () => {
          panel.style.height = "";
        },
      },
    );
  } else {
    gsap.to(panel, {
      height: 0,
      opacity: 0,
      duration: 0.28,
      ease: "power2.inOut",
      onComplete: () => {
        panel.hidden = true;
        panel.style.height = "";
      },
    });
  }
}

function initParkingOptions(section) {
  if (!section || section.__parkingOptionsInit) return;
  section.__parkingOptionsInit = true;

  const items = Array.from(section.querySelectorAll(".parking-options__item"));
  if (!items.length) return;

  // Set initial hidden state
  items.forEach((item) => {
    const panel = item.querySelector(".parking-options__panel");
    if (!panel) return;

    const open = item.classList.contains("is-open");
    panel.hidden = !open;
  });

  // Enforce only one open by default (keep the first open)
  const openItems = items.filter((it) => it.classList.contains("is-open"));
  if (openItems.length > 1) {
    openItems.slice(1).forEach((it) => toggleItem(it, false));
  }

  // Accordion behavior: only one open at a time
  items.forEach((item) => {
    const btn = item.querySelector(".parking-options__trigger");
    const panel = item.querySelector(".parking-options__panel");
    if (!btn || !panel) return;

    btn.addEventListener("click", () => {
      const isOpen = item.classList.contains("is-open");

      // If clicking currently open item -> close it
      if (isOpen) {
        toggleItem(item, false);
        return;
      }

      // Close all other open items in THIS section
      items.forEach((other) => {
        if (other !== item && other.classList.contains("is-open")) {
          toggleItem(other, false);
        }
      });

      // Open clicked
      toggleItem(item, true);
    });
  });

  // Reveal
  const eyebrow = section.querySelector(".parking-options__eyebrow");
  const headline = section.querySelector(".parking-options__headline");
  const intro = section.querySelector(".parking-options__intro");
  const tips = section.querySelector(".parking-options__tips");
  const image = section.querySelector(".parking-options__image");
  const right = section.querySelector(".parking-options__right");

  gsap.set([eyebrow, headline, intro, tips, image, right], {
    opacity: 0,
    y: 18,
  });

  const tlOpts = isEditor
    ? { defaults: { ease: "power3.out" } }
    : {
        defaults: { ease: "power3.out" },
        scrollTrigger: { trigger: section, start: "top 78%", once: true },
      };

  const tl = gsap.timeline(tlOpts);
  if (eyebrow) tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.45 });
  if (headline) tl.to(headline, { opacity: 1, y: 0, duration: 0.65 }, "-=0.2");
  if (intro) tl.to(intro, { opacity: 1, y: 0, duration: 0.55 }, "-=0.25");
  if (tips) tl.to(tips, { opacity: 1, y: 0, duration: 0.45 }, "-=0.25");
  if (image) tl.to(image, { opacity: 1, y: 0, duration: 0.45 }, "-=0.25");
  if (right) tl.to(right, { opacity: 1, y: 0, duration: 0.55 }, "-=0.15");
}

const mount = () => {
  if (!isEditor) {
    document.querySelectorAll(".parking-options").forEach(initParkingOptions);
    return;
  }

  const root = document.querySelector(".editor-styles-wrapper") || document;
  const run = () =>
    root.querySelectorAll(".parking-options").forEach(initParkingOptions);

  run();
  const mo = new MutationObserver(run);
  mo.observe(root, { childList: true, subtree: true });
};

if (window.wp?.domReady) window.wp.domReady(mount);
else document.addEventListener("DOMContentLoaded", mount);
